/**
 * DRPX Courier — DB-Mapper-Definitionen für Supabase
 *
 * Jeder `map*FromDb`-Export ist ein Mapper, der von `createMapper` erzeugt
 * wird und Supabase-Zeilen (snake_case, JSONB-Felder etc.) in das App-interne
 * camelCase-Format umwandelt.
 *
 * ─── Typ-Bindung an der Grenze ────────────────────────────────────────
 *
 * `createMapper` (siehe `lib/mapper.ts`) ist bewusst pragmatisch typisiert:
 * rein `Record<string, unknown> | null | undefined`, raus `Record<string,
 * unknown> | null`. Die Bindung an echte Domain-Typen passiert hier an der
 * Grenze via expliziter Type-Assertion (`as unknown as DomainMapper<T>`).
 *
 * Der Double-Cast über `unknown` ist nötig, weil TypeScript den direkten Cast
 * von `Record<string, unknown>` auf konkrete Interfaces (mit festen Feldern)
 * nicht erlaubt. Das ist kein Missbrauch des Typsystems, sondern der saubere
 * Weg, um an **einer Stelle** die Brücke zwischen generischem Mapping und
 * domänenspezifischen Typen zu schlagen.
 *
 * Mapper ohne zugehörigen Domain-Typ (z.B. `mapNotificationFromDb`,
 * `mapVorlageFromDb`) behalten den generischen `MapperFn`-Return-Typ. Sie
 * werden getypt, sobald das konsumierende Modul migriert wird und dort ein
 * passendes Domain-Interface entsteht.
 *
 * @module lib/supabase/mappers
 */

import { createMapper } from '../mapper.js';
import type {
  Profile,
  Auftrag,
  Angebot,
  Bewertung,
  FahrerKunde,
  FahrerAuftrag,
  FahrerRechnung,
  FahrerBuchung,
  Fahrt,
} from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interner Helper-Typ: Mapper mit Domain-Typ als Return
// ════════════════════════════════════════════════════════════════════

/**
 * Mapper-Funktion mit Domain-Typ am Ausgang.
 *
 * Signatur identisch zu `MapperFn` aus `mapper.ts`, nur dass der Return-Typ
 * an ein konkretes Domain-Interface gebunden ist. Wird lokal über ein
 * Double-Cast via `unknown` erzeugt — siehe Dateikommentar oben.
 */
type DomainMapper<T> = (row: Record<string, unknown> | null | undefined) => T | null;

// ════════════════════════════════════════════════════════════════════
// Interne Helper für `computed`-Blöcke
// ════════════════════════════════════════════════════════════════════

/**
 * Sichere String-Extraktion aus unbekannten Row-Feldern.
 * Gibt den Wert nur dann zurück, wenn er tatsächlich ein String ist —
 * sonst leerer String. Nötig, weil `row[key]` im strict-Mode `unknown` ist
 * und direkte String-Operationen (`.replace`, `.slice`) nicht erlaubt sind.
 */
function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * Konvertiert einen Row-Wert zu `number | null`.
 * Null/undefined bleiben null, alles andere wird durch `Number()` gecastet.
 * Entspricht dem Original-Muster `row.x !== null ? Number(row.x) : null`.
 */
function numOrNull(v: unknown): number | null {
  return v === null || v === undefined ? null : Number(v);
}

/**
 * Preis eigener Aufträge (23.10.2026): `fahrer_auftraege.preis` ist eine
 * TEXT-Spalte, kam also als „150" an, obwohl `FahrerAuftrag.preis` eine Zahl
 * ist. Summen hängten deshalb Texte aneinander („150" + „150" = „150150").
 * Leer/ungültig → `undefined` (= kein Preis, Formular bleibt leer);
 * Komma als Dezimaltrenner wird verstanden („150,50" → 150.5).
 *
 * @param v - Wert aus der Datenbank
 * @returns Preis als Zahl oder undefined
 */
export function preisOderLeer(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v !== 'string' || v.trim() === '') return undefined;
  const s = v.trim();
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : undefined;
}

// ════════════════════════════════════════════════════════════════════
// _drpxNr — Kurz-Auftragsnummer aus DB-Row
// ════════════════════════════════════════════════════════════════════

/**
 * Erzeugt eine Auftragsnummer aus einer DB-Row.
 * Nutzt `row.nr` wenn vorhanden, sonst einen Fallback aus den ersten
 * 8 Zeichen der UUID (ohne Bindestriche, uppercase).
 *
 * @param row DB-Row mit optionalen Feldern `nr` und `id`
 * @returns Auftragsnummer (z.B. `DRPX-25-4-01234` oder `DRPX-A1B2C3D4`)
 */
export function _drpxNr(row: Record<string, unknown>): string {
  const nr = str(row.nr);
  if (nr) return nr;
  return 'DRPX-' + str(row.id).replace(/-/g, '').slice(0, 8).toUpperCase();
}

// ════════════════════════════════════════════════════════════════════
// Domain-gebundene Mapper
// ════════════════════════════════════════════════════════════════════

/** Mappt ein Supabase-Profil (profiles) → App-Format. */
export const mapProfileFromDb = createMapper({
  defaults: {
    transportAnweisungen: '',
    aboInterval: 'monat',
    kycGewerbeschein: null,
    kycVersicherung: null,
    kycBonitaetsnachweis: null,
    dokReviews: null,
    zertReviews: {},
    adminNotiz: '',
    adminMessages: [],
    favoriten: [],
    blockedUsers: [],
    notifPrefs: {},
    iban: '',
    bic: '',
    bankName: '',
    kennzeichen: '',
    fahrzeugTyp: '',
    ausstattung: {},
    einsatzgebietGeo: null,
    einsatzgebietRadius: 100,
    lastActive: null,
    nutzerNr: null,
    aboTier: 'standard',
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    avvAkzeptiertAm: null,
  },
  booleans: ['aboPausiert', 'aboGesperrt', 'kleinunternehmer', 'darfBieten', 'bonitaetGeprueft'],
  computed: (_mapped, row) => ({
    name: str(row.firma) || str(row.login_name) || str(row.email),
    aboGutschrift: numOrNull(row.abo_gutschrift),
    aboErstattung: numOrNull(row.abo_erstattung),
  }),
}) as unknown as DomainMapper<Profile>;

/** Mappt einen Supabase-Auftrag (auftraege) → App-Format. */
export const mapAuftragFromDb = createMapper({
  defaults: {
    kundenRef: '',
    ausstattung: {},
    beladeStopps: [],
    entladeStopps: [],
    empfangenVon: '',
    entladeDatum: null,
    entladeZeit: '',
    anzahl: '',
    volumen: '',
    packstuecke: [],
    abholFirma: '',
    abholAnsprech: '',
    abholTel: '',
    zielFirma: '',
    zielAnsprech: '',
    zielTel: '',
    verlauf: [],
    trackingToken: '',
    firmaSeenAt: null,
    firmaBonitaetGeprueft: false,
  },
  numbers: ['festpreis'],
  computed: (_mapped, row) => ({
    nr: _drpxNr(row),
    vergebenPreis: numOrNull(row.vergeben_preis),
    angebote: [],
  }),
}) as unknown as DomainMapper<Auftrag>;

/** Mappt ein Supabase-Angebot (angebote) → App-Format. */
export const mapAngebotFromDb = createMapper({
  defaults: { kennzeichen: '', fehlendeZertifikate: [], fahrzeugTyp: '' },
  numbers: ['preis'],
  booleans: ['istFestpreis', 'akzeptiert'],
}) as unknown as DomainMapper<Angebot>;

/** Mappt eine Supabase-Bewertung (bewertungen) → App-Format. */
export const mapBewertungFromDb = createMapper({
  defaults: { attribute: null },
}) as unknown as DomainMapper<Bewertung>;

/** Mappt einen Fahrer-Kunden (fahrer_kunden) → App-Format. */
export const mapFahrerKundeFromDb = createMapper({
  defaults: {
    ansprech: '',
    telefon: '',
    email: '',
    adresse: '',
    plz: '',
    ort: '',
    notizen: '',
  },
}) as unknown as DomainMapper<FahrerKunde>;

/** Mappt einen Fahrer-eigenen Auftrag (fahrer_auftraege) → App-Format. */
export const mapFahrerAuftragFromDb = createMapper({
  defaults: {
    titel: '',
    kundeId: '',
    kundeName: '',
    abhol: '',
    ziel: '',
    datum: '',
    zeit: '',
    entladeZeit: '',
    gewicht: '',
    groesse: '',
    notizen: '',
    status: 'offen',
    packstuecke: [],
    fotoDoku: [],
  },
  // Spalte `nr` → App-Feld `auftragNr` (11.10.2026). Vorher kam sie als `nr`
  // an, die Karten lasen aber `auftragNr` und zeigten deshalb die UUID.
  computed: (_mapped, row) => ({
    erstelltAm: str(row.created_at),
    auftragNr: str(row.nr),
    preis: preisOderLeer(row.preis),
  }),
}) as unknown as DomainMapper<FahrerAuftrag>;

/** Mappt eine Fahrer-Rechnung (fahrer_rechnungen) → App-Format. */
export const mapFahrerRechnungFromDb = createMapper({
  defaults: {
    rechnungsNr: '',
    kundeId: '',
    kundeName: '',
    kundeAdresse: '',
    kundePlz: '',
    kundeOrt: '',
    kundeEmail: '',
    kundeTel: '',
    auftragId: '',
    datum: '',
    leistungsdatum: '',
    faellig: '',
    positionen: [],
    notizen: '',
    zahlungsziel: '',
    kundenRef: '',
    kennzeichen: '',
    route: '',
    skonto: '',
    bankIban: '',
    bankBic: '',
    bankName: '',
    steuernr: '',
    status: 'offen',
    letzteMahnung: null,
  },
  numbers: ['sumNetto', 'sumMwst', 'sumBrutto', 'mahnstufe'],
  booleans: ['kleinunternehmer', 'locked'],
  computed: (_mapped, row) => ({ erstelltAm: str(row.created_at) }),
}) as unknown as DomainMapper<FahrerRechnung>;

/** Mappt eine Fahrer-Buchung (fahrer_buchungen) → App-Format. */
export const mapFahrerBuchungFromDb = createMapper({
  defaults: {
    auftragId: '',
    datum: '',
    typ: 'einnahme',
    kategorie: '',
    beschreibung: '',
    kunde: '',
    quelle: '',
  },
  numbers: ['betrag'],
}) as unknown as DomainMapper<FahrerBuchung>;

/** Mappt eine Fahrt (fahrer_fahrten) → App-Format. */
export const mapFahrtFromDb = createMapper({
  defaults: {
    datum: '',
    startOrt: '',
    zielOrt: '',
    kmStart: '',
    kmEnde: '',
    kunde: '',
    zweck: '',
    kennzeichen: '',
    notizen: '',
    quellId: '',
  },
  numbers: ['kmGefahren'],
}) as unknown as DomainMapper<Fahrt>;

// ════════════════════════════════════════════════════════════════════
// Noch ungebundene Mapper (generischer Return-Typ)
// ────────────────────────────────────────────────────────────────────
// Diese Mapper behalten den Default-Rückgabetyp `Record<string, unknown> |
// null` aus `MapperFn`. Sobald das konsumierende Modul migriert wird und
// dort ein passendes Domain-Interface entsteht (in `types.ts`), kommt die
// Bindung hier genau wie bei den Mappern oben nach.
// ════════════════════════════════════════════════════════════════════

/** Mappt eine Supabase-Benachrichtigung (notifications) → App-Format. */
export const mapNotificationFromDb = createMapper({});

/** Mappt eine Firma-Rechnungsvorlage (firma_vorlagen) → App-Format. */
export const mapVorlageFromDb = createMapper({
  defaults: {
    name: '',
    titel: '',
    abhol: '',
    ziel: '',
    geoAbhol: null,
    geoZiel: null,
    beladeStopps: [],
    entladeStopps: [],
    fahrzeug: 'transporter',
    ausstattung: {},
    gewicht: '',
    groesse: '',
    notizen: '',
    abholFirma: '',
    abholAnsprech: '',
    abholTel: '',
    zielFirma: '',
    zielAnsprech: '',
    zielTel: '',
    kundenRef: '',
    intervall: '',
    uhrzeit: '08:00',
    naechsteErstellung: null,
    letzteErstellung: null,
  },
  numbers: ['festpreis'],
  booleans: ['wiederkehrend'],
  computed: (_mapped, row) => ({ aktiv: row.aktiv !== false }),
});

/** Mappt eine Fahrer-Rechnungsvorlage (fahrer_vorlagen) → App-Format. */
export const mapFahrerVorlageFromDb = createMapper({
  defaults: {
    kundeName: '',
    kundeId: '',
    abhol: '',
    ziel: '',
    preis: '',
    gewicht: '',
    groesse: '',
    notizen: '',
  },
});

/** Mappt ein Auftragsfoto (auftrag_fotos) → App-Format. */
export const mapAuftragFotoFromDb = createMapper({
  defaults: { auftragId: '', stoppKey: '', filePath: '', notiz: '' },
  computed: () => ({ signedUrl: null }),
});

/** Mappt eine Firma-Adresse (firma_adressbuch) → App-Format. */
export const mapFirmaAdresseFromDb = createMapper({
  defaults: {
    name: '',
    adresse: '',
    plz: '',
    ort: '',
    land: 'DE',
    firma: '',
    ansprech: '',
    telefon: '',
    email: '',
    notizen: '',
  },
});

/** Mappt ein Fahrer-Zertifikat (fahrer_zertifikate) → App-Format. */
export const mapFahrerZertifikatFromDb = createMapper({
  defaults: {
    zertKey: '',
    dateiName: '',
    dateiGroesse: 0,
    hochgeladenAm: '',
    gueltigBis: '',
    status: 'pruefung',
    filePath: '',
  },
});

/** Mappt einen Audit-Log-Eintrag (audit_log) → App-Format. */
export const mapAuditLogFromDb = createMapper({
  defaults: { userId: null, aktion: '', details: null },
  computed: (_mapped, row) => {
    const createdAt = str(row.created_at);
    return {
      zeitpunkt: createdAt,
      datum: createdAt ? createdAt.slice(0, 10) : '',
    };
  },
});

/** Mappt eine Profil-Textvorlage (profil_textvorlagen) → App-Format. */
export const mapProfilTextvorlageFromDb = createMapper({
  defaults: { name: '', text: '' },
});

/** Mappt eine Chat-Nachricht (auftrag_nachrichten) → App-Format. */
export const mapChatNachrichtFromDb = createMapper({
  booleans: ['gelesenVonFirma', 'gelesenVonFahrer'],
});

// ════════════════════════════════════════════════════════════════════
// dataUrlToBlob — Utility für Foto-Upload
// ════════════════════════════════════════════════════════════════════

/**
 * Konvertiert eine Data-URL (z.B. aus einem Canvas oder FileReader) in einen
 * Blob. Wird beim Foto-Upload verwendet: Kamera liefert
 * `data:image/jpeg;base64,…`, der Blob kann direkt in Supabase Storage
 * hochgeladen werden.
 *
 * Wirft einen aussagekräftigen Fehler, wenn der MIME-Prefix fehlt. (Der alte
 * JS-Code crashte an derselben Stelle mit einem nichtssagenden
 * `TypeError: Cannot read properties of null`.)
 *
 * @param dataUrl Vollständige Data-URL inkl. MIME-Prefix
 * @returns Binärer Blob mit korrektem MIME-Typ
 * @throws Error wenn die Data-URL keinen MIME-Prefix enthält
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const match = parts[0].match(/:(.*?);/);
  if (!match) {
    throw new Error('dataUrlToBlob: invalid data URL — missing MIME prefix');
  }
  const mime = match[1];
  const raw = atob(parts[1]);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
